import { Notification } from '../types';

export type SemanticCategory =
  | 'TASK'
  | 'PAYMENT'
  | 'ROUTE'
  | 'DOCUMENT'
  | 'APPLICATION'
  | 'FINANCE'
  | 'GENERAL';

export type SemanticTier = 'INFO' | 'ACTION_REQUIRED' | 'WARNING' | 'CRITICAL';

const FINANCE_CATEGORIES = new Set<SemanticCategory>(['PAYMENT', 'FINANCE']);

export function getSemanticCategory(notification: Notification): SemanticCategory {
  const fromData = String((notification.data as Record<string, unknown>)?.semanticCategory || '').toUpperCase();
  if (fromData === 'TASK' || fromData === 'PAYMENT' || fromData === 'ROUTE' || fromData === 'DOCUMENT') {
    return fromData as SemanticCategory;
  }
  if (fromData === 'APPLICATION' || fromData === 'FINANCE') return fromData as SemanticCategory;
  const eventType = String((notification.data as Record<string, unknown>)?.eventType || '').toUpperCase();
  if (eventType.includes('PAYMENT') || eventType.includes('OBLIGATION')) return 'PAYMENT';
  if (eventType.includes('ROUTE')) return 'ROUTE';
  if (eventType.includes('DOC')) return 'DOCUMENT';
  if (notification.taskId || (notification.data as { taskId?: number })?.taskId) return 'TASK';
  return 'GENERAL';
}

export function getSemanticTier(notification: Notification): SemanticTier {
  const fromData = String((notification.data as Record<string, unknown>)?.semanticTier || '').toUpperCase();
  if (
    fromData === 'INFO' ||
    fromData === 'ACTION_REQUIRED' ||
    fromData === 'WARNING' ||
    fromData === 'CRITICAL'
  ) {
    return fromData as SemanticTier;
  }
  const p = String(notification.priority || (notification.data as Record<string, unknown>)?.priority || '').toUpperCase();
  if (p === 'CRITICAL') return 'CRITICAL';
  if (p === 'HIGH') return 'WARNING';
  if (notification.taskId) return 'ACTION_REQUIRED';
  return 'INFO';
}

export function getDisplayTitle(notification: Notification): string {
  return String(notification.title || 'Уведомление');
}

export function getDisplaySubtitle(notification: Notification): string {
  const data = notification.data as Record<string, unknown> | undefined;
  const subtitle = String(data?.subtitle || '').trim();
  if (subtitle) return subtitle;
  return String(notification.message || '');
}

export function getContextBadges(notification: Notification): string[] {
  const data = (notification.data || {}) as Record<string, unknown>;
  const badges: string[] = [];
  const category = getSemanticCategory(notification);

  const appNum = data.applicationNumber ? String(data.applicationNumber) : '';
  const appId = Number(data.applicationId ?? 0);
  if (appNum) badges.push(`Заявка №${appNum}`);
  else if (appId > 0) badges.push(`Заявка #${appId}`);

  const routeId = Number(data.routeId ?? (data.entityType === 'ROUTE' ? data.entityId : 0) ?? 0);
  if (routeId > 0) badges.push(`Маршрут #${routeId}`);

  if (category === 'PAYMENT' || category === 'FINANCE') badges.push('FINANCE');
  else if (category === 'ROUTE') badges.push('ROUTE');
  else if (category === 'APPLICATION') badges.push('CLIENT');
  else if (category === 'DOCUMENT') badges.push('DOC');
  else if (category === 'TASK') badges.push('TASK');

  return Array.from(new Set(badges)).slice(0, 3);
}

export function getTierAccentColor(tier: SemanticTier): string {
  if (tier === 'CRITICAL') return '#ff4d4f';
  if (tier === 'WARNING') return '#fa8c16';
  if (tier === 'ACTION_REQUIRED') return '#faad14';
  return '#1890ff';
}

export function getCategoryIconColor(category: SemanticCategory, tier: SemanticTier): string {
  if (tier === 'CRITICAL') return '#ff4d4f';
  if (tier === 'WARNING' || tier === 'ACTION_REQUIRED') return '#fa8c16';
  if (category === 'PAYMENT' || category === 'FINANCE') return '#52c41a';
  if (category === 'ROUTE') return '#1677ff';
  if (category === 'DOCUMENT') return '#722ed1';
  return '#1890ff';
}

/** Relative time in Russian for bell UX. */
export function formatRelativeTimeRu(dateString: string): string {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return 'только что';
  if (diffMin < 60) return `${diffMin} мин назад`;

  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours} ч назад`;

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (startOfDate.getTime() === startOfYesterday.getTime()) return 'вчера';

  const diffDays = Math.floor((startOfToday.getTime() - startOfDate.getTime()) / 86400000);
  if (diffDays < 7) return `${diffDays} дн назад`;

  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

export type GroupedNotification = Notification & {
  _groupCount?: number;
  _groupIds?: number[];
};

function financeGroupKey(n: Notification): string | null {
  const cat = getSemanticCategory(n);
  if (!FINANCE_CATEGORIES.has(cat)) return null;
  const hourBucket = new Date(n.createdAt).toISOString().slice(0, 13);
  return `finance:${hourBucket}`;
}

/** Simple stacking: finance events per hour; entity+title for others. */
export function groupNotificationsForBell(list: Notification[]): GroupedNotification[] {
  const financeBuckets = new Map<string, GroupedNotification[]>();
  const others: Notification[] = [];

  for (const n of list) {
    const fk = financeGroupKey(n);
    if (fk) {
      const arr = financeBuckets.get(fk) || [];
      arr.push(n);
      financeBuckets.set(fk, arr);
    } else {
      others.push(n);
    }
  }

  const result: GroupedNotification[] = [];

  for (const [, items] of Array.from(financeBuckets.entries())) {
    const sorted = [...items].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    if (sorted.length >= 3) {
      const latest = sorted[0];
      result.push({
        ...latest,
        title: `${sorted.length} новых финансовых события`,
        message: getDisplaySubtitle(latest),
        _groupCount: sorted.length,
        _groupIds: sorted.map((x) => x.id),
        data: {
          ...(latest.data || {}),
          subtitle: sorted.map((x) => getDisplaySubtitle(x)).slice(0, 3).join(' · ')
        }
      });
    } else {
      result.push(...sorted);
    }
  }

  const entityMap = new Map<string, GroupedNotification>();
  for (const n of others) {
    const entityType = String((n.data as Record<string, unknown>)?.entityType || '');
    const entityId = String((n.data as Record<string, unknown>)?.entityId || '');
    const key =
      entityType && entityId
        ? `${entityType}:${entityId}:${String(n.title || '')}:${getSemanticTier(n)}`
        : `notification:${n.id}`;
    const existing = entityMap.get(key);
    if (!existing) {
      entityMap.set(key, { ...n, _groupCount: 1, _groupIds: [n.id] });
    } else {
      const nextCount = (existing._groupCount || 1) + 1;
      if (new Date(n.createdAt).getTime() > new Date(existing.createdAt).getTime()) {
        entityMap.set(key, { ...n, _groupCount: nextCount, _groupIds: [...(existing._groupIds || [existing.id]), n.id] });
      } else {
        entityMap.set(key, { ...existing, _groupCount: nextCount, _groupIds: [...(existing._groupIds || [existing.id]), n.id] });
      }
    }
  }

  result.push(...Array.from(entityMap.values()));
  return result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

export function getUnreadItemClassName(isRead: boolean): string {
  return isRead ? 'notification-item notification-item--read' : 'notification-item notification-item--unread';
}

export function canOpenNotification(notification: Notification): boolean {
  const taskId = Number(notification.taskId ?? (notification.data as { taskId?: number })?.taskId ?? 0);
  if (Number.isFinite(taskId) && taskId > 0) return true;
  return Boolean(String((notification.data as Record<string, unknown>)?.actionLink || '').trim());
}
